import React, { useEffect, useRef, useState } from 'react';

// Inline rename for a clients.Task's `name`.
//
// Imported and rolled-forward names carry years ("Smith 2025 T2") and there
// was no way to change them in the app. A blank name is meaningful: the Tasks
// grid shows `name || client_name`, so clearing the field means "just use the
// client's name" -- the display says so rather than showing nothing.
//
// `onSave(name)` receives the trimmed value (possibly '') and should return a
// promise; a rejection keeps the editor open with the error shown.
export default function TaskNameEditor({ name, clientName, onSave }) {
  const current = name || '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const start = () => {
    setDraft(current);
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = async () => {
    if (busy) return;
    const next = draft.trim();
    if (next === current.trim()) {
      cancel();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      setError((e && e.message) || 'Could not rename the task.');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginTop: 6 }}>
        <span style={{ color: '#6b7280' }}>Task name:</span>
        <span data-testid="task-name-display" style={{ fontWeight: 500 }}>
          {/* Quoted, so it reads as the stored value -- and stays distinct
              from the page heading, which often shows the same words. */}
          {current ? `“${current}”` : (clientName || '—')}
          {!current && (
            <span style={{ color: '#9ca3af', fontWeight: 400 }}> (client name)</span>
          )}
        </span>
        <button
          type="button"
          aria-label="Rename task"
          title="Rename task"
          onClick={start}
          style={{
            border: '1px solid #d1d5db', background: 'white', borderRadius: 4,
            padding: '1px 6px', cursor: 'pointer', fontSize: 12, color: '#374151',
          }}
        >
          ✎ Rename
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <input
          ref={inputRef}
          type="text"
          aria-label="Task name"
          value={draft}
          placeholder={clientName || ''}
          disabled={busy}
          maxLength={255}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          style={{
            fontSize: 14, padding: '4px 8px', border: '1px solid #93c5fd',
            borderRadius: 4, minWidth: 280,
          }}
        />
        <button
          type="button"
          onClick={save}
          disabled={busy}
          style={{
            padding: '4px 10px', border: 'none', borderRadius: 4,
            background: '#0073ea', color: 'white', cursor: busy ? 'wait' : 'pointer', fontSize: 13,
          }}
        >
          Save
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={busy}
          style={{
            padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 4,
            background: 'white', cursor: 'pointer', fontSize: 13,
          }}
        >
          Cancel
        </button>
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
        Leave blank to use the client name. Enter saves, Esc cancels.
      </div>
      {error && (
        <div role="alert" style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{error}</div>
      )}
    </div>
  );
}
